import { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { CheckCircleFill, ChevronDown, Clock, People } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import {
  formatTimeRange,
  getRecipeImageCandidates,
  getRecipeImagePosition,
  normalize,
  normalizeSection
} from '../utils/recipes';
import {
  buildWeeklySavingsLabel,
  getSortedMatchedOffers
} from '../utils/recommendations';
import { fetchJsonOrThrow } from '../utils/fetchJson';
import { loadPantryState, pantryStateToItems } from '../utils/pantryStorage';
import { buildRecipePriceComparison } from '../utils/recipePricing';
import { buildRecipeSearchTokens, compareRecipesByPantryPrice, matchesRecipeSearch } from '../utils/recipeSearch';
import api_url from '../config/api_url';
import FavoriteButton from '../components/FavoriteButton';

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 420'%3E%3Crect width='640' height='420' fill='%23f4e7d8'/%3E%3Ctext x='50%25' y='50%25' fill='%23845a2f' font-size='28' text-anchor='middle' dominant-baseline='middle'%3EIntet billede%3C/text%3E%3C/svg%3E";

const RECIPE_SEARCH_STATE_KEY = 'recipe-search-state';
const DEFAULT_SORT_OPTION = 'best-weekly-deals';
const SORT_OPTIONS = [
  { value: 'best-weekly-deals', label: 'Bedste tilbudsretter i denne uge' },
  { value: 'cheapest-with-pantry', label: 'Billigst med det, jeg har i spisekammeret' },
  { value: 'best-value', label: 'Mest værdi for pengene' },
  { value: 'healthy-deals', label: 'Bedste sunde valg på tilbud' },
  { value: 'classic-weeknight', label: 'Bedste klassiske hverdagsretter' },
  { value: 'cheap-filling', label: 'Billige retter med høj mæthed' }
];

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function loadRecipeSearchState() {
  if (typeof window === 'undefined') {
    return {
      searchTerm: '',
      sortOption: DEFAULT_SORT_OPTION,
      scrollRecipeId: ''
    };
  }

  try {
    const raw = window.sessionStorage.getItem(RECIPE_SEARCH_STATE_KEY);
    if (!raw) {
      return {
        searchTerm: '',
        sortOption: DEFAULT_SORT_OPTION,
        scrollRecipeId: ''
      };
    }

    const parsed = JSON.parse(raw);
    return {
      searchTerm: normalize(parsed?.searchTerm),
      sortOption: normalize(parsed?.sortOption) || DEFAULT_SORT_OPTION,
      scrollRecipeId: normalize(parsed?.scrollRecipeId)
    };
  } catch (error) {
    return {
      searchTerm: '',
      sortOption: DEFAULT_SORT_OPTION,
      scrollRecipeId: ''
    };
  }
}

function saveRecipeSearchState(state) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(
    RECIPE_SEARCH_STATE_KEY,
    JSON.stringify({
      searchTerm: normalize(state?.searchTerm),
      sortOption: normalize(state?.sortOption) || DEFAULT_SORT_OPTION,
      scrollRecipeId: normalize(state?.scrollRecipeId)
    })
  );
}

function getRecipeCardScore(card, sortOption) {
  const dealScore = card.weeklyDealScore || 0;
  const valueScore = card.valueScore || 0;
  const healthScore = card.healthScore || 0;
  const weekdayScore = card.weekdayScore || 0;
  const overallScore = card.overallScore || 0;
  const savings = card.savings || 0;
  const weeklyPrice = card.weeklyPrice ?? 999;
  const protein = card.protein || 0;
  const fibre = card.fibre || 0;
  const cookTime = card.cookTime ?? 90;
  const profileBoost =
    /komfort|hverdag|klassisk/.test(card.mealProfile) || /gryde|frikadeller|fars|pasta/.test(card.recipeType)
      ? 12
      : 0;

  switch (sortOption) {
    case 'best-value':
      return valueScore * 1.3 + savings * 0.6 - weeklyPrice * 0.25 + card.matchedOfferCount * 3 + overallScore * 0.2;
    case 'healthy-deals':
      return healthScore * 1.2 + dealScore * 0.5 + protein * 0.6 + fibre * 0.8 - weeklyPrice * 0.1;
    case 'classic-weeknight':
      return weekdayScore * 1.3 + profileBoost - cookTime * 0.25 + overallScore * 0.2;
    case 'cheap-filling':
      return protein * 1.1 + fibre * 1.3 - weeklyPrice * 0.35 + valueScore * 0.6 + weekdayScore * 0.2;
    case 'best-weekly-deals':
    default:
      return dealScore * 1.2 + card.matchedOfferCount * 8 + savings * 0.5 + overallScore * 0.4;
  }
}

function buildOfferSavingsText(weeklySavingsLabel) {
  const savingsText = normalize(weeklySavingsLabel)
    .replace(/^Spar\b/, 'spar')
    .replace(/\s+i denne uge$/i, '');
  if (!savingsText) {
    return '';
  }

  return `Spar ${savingsText.replace(/^spar\s*/i, '')}`;
}

function RecipeSearch() {
  const [recipes, setRecipes] = useState([]);
  const [weeklyRecommendations, setWeeklyRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(() => loadRecipeSearchState().searchTerm);
  const [sortOption, setSortOption] = useState(() => loadRecipeSearchState().sortOption);
  const [pantryState, setPantryState] = useState(() => loadPantryState());

  useEffect(() => {
    const loadRecipes = async () => {
      setLoading(true);
      setError('');

      try {
        const recommendationsPromise = fetchJsonOrThrow(api_url + 'api/recommendations/weekly').catch(() => null);
        const recipesJson = await fetchJsonOrThrow(api_url + 'api/recipes');
        const recommendationsJson = await recommendationsPromise;

        setRecipes(Array.isArray(recipesJson) ? recipesJson : []);
        setWeeklyRecommendations(
          recommendationsJson && typeof recommendationsJson === 'object' ? recommendationsJson : null
        );
      } catch (fetchError) {
        setError('Opskrifterne kunne ikke hentes lige nu.');
      } finally {
        setLoading(false);
      }
    };

    loadRecipes().catch(() => {
      setLoading(false);
      setError('Kunne ikke hente opskrifter.');
    });
  }, []);

  useEffect(() => {
    const syncPantryState = () => setPantryState(loadPantryState());

    window.addEventListener('focus', syncPantryState);
    window.addEventListener('storage', syncPantryState);

    return () => {
      window.removeEventListener('focus', syncPantryState);
      window.removeEventListener('storage', syncPantryState);
    };
  }, []);

  const recommendationsById = useMemo(() => {
    const rankedCandidates = Array.isArray(weeklyRecommendations?.ranked_candidates)
      ? weeklyRecommendations.ranked_candidates
      : [];

    return new Map(
      rankedCandidates.map((candidate) => [normalize(candidate.recipe_id).toLowerCase(), candidate])
    );
  }, [weeklyRecommendations]);
  const pantryItems = useMemo(() => pantryStateToItems(pantryState), [pantryState]);

  useEffect(() => {
    saveRecipeSearchState({
      ...loadRecipeSearchState(),
      searchTerm,
      sortOption
    });
  }, [searchTerm, sortOption]);

  const recipeCards = useMemo(() => {
    return recipes.map((recipe) => {
      const recipeId = recipe.recipe_id;
      const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
      const recommendation = recommendationsById.get(normalize(recipeId).toLowerCase());
      const matchedOffers = getSortedMatchedOffers(recipe, recommendation);
      const optionalCount = ingredients.filter(
        (ingredient) => normalizeSection(ingredient.sektion) === 'ekstra'
      ).length;
      const pricingSource = recommendation || recipe;
      const priceComparison = buildRecipePriceComparison(recipe, { pricingSource, pantryItems });

      return {
        recipe,
        recipeId,
        ingredients,
        recommendation,
        matchedOffers,
        matchedOfferCount: matchedOffers.length,
        optionalCount,
        pricingSource,
        priceComparison,
        weeklySavingsLabel: buildWeeklySavingsLabel(pricingSource),
        weeklyDealScore: toNumber(recommendation?.uge_tilbudsrelevans_score_0_100),
        valueScore: toNumber(recommendation?.uge_prisvaerdi_score_0_100_input),
        healthScore: toNumber(recommendation?.ernaeringsscore_0_100),
        weekdayScore: toNumber(recommendation?.hverdagsscore_0_100),
        overallScore: toNumber(recommendation?.samlet_score_foreloebig_0_100),
        weeklyPrice: toNumber(pricingSource?.public_price?.pris_denne_uge_dkk),
        savings: toNumber(pricingSource?.public_price?.besparelse_dkk),
        protein: toNumber(recipe?.nutrition?.protein_pr_portion_g),
        fibre: toNumber(recipe?.nutrition?.fibre_pr_portion_g),
        cookTime:
          toNumber(recipe?.tid_max) ??
          toNumber(recipe?.tid_min) ??
          toNumber(recommendation?.total_tid_min) ??
          toNumber(recommendation?.aktiv_tid_min),
        mealProfile: normalize(recommendation?.maaltidsprofil || recipe?.maaltidsprofil).toLowerCase(),
        recipeType: normalize(recommendation?.ret_type || recipe?.ret_type).toLowerCase(),
        searchTokens: buildRecipeSearchTokens(recipe)
      };
    });
  }, [recipes, recommendationsById, pantryItems]);

  const visibleRecipeCards = useMemo(() => {
    return recipeCards
      .filter((card) => matchesRecipeSearch(card.searchTokens, searchTerm))
      .sort((left, right) => {
        if (sortOption === 'cheapest-with-pantry') {
          return compareRecipesByPantryPrice(left, right)
            || normalize(left.recipe.name).localeCompare(normalize(right.recipe.name), 'da-DK');
        }

        const leftScore = getRecipeCardScore(left, sortOption);
        const rightScore = getRecipeCardScore(right, sortOption);
        return (
          rightScore - leftScore ||
          (right.matchedOfferCount || 0) - (left.matchedOfferCount || 0) ||
          normalize(left.recipe.name).localeCompare(normalize(right.recipe.name), 'da-DK')
        );
      });
  }, [recipeCards, searchTerm, sortOption]);

  useEffect(() => {
    if (loading || error) {
      return;
    }

    const { scrollRecipeId } = loadRecipeSearchState();
    if (!scrollRecipeId) {
      return;
    }

    const target = document.querySelector(`[data-recipe-id="${scrollRecipeId}"]`);
    saveRecipeSearchState({
      ...loadRecipeSearchState(),
      scrollRecipeId: ''
    });

    if (!target) {
      return;
    }

    window.requestAnimationFrame(() => {
      target.scrollIntoView({ block: 'center', behavior: 'auto' });
    });
  }, [loading, error, visibleRecipeCards.length]);

  const hasActiveFilters = Boolean(normalize(searchTerm) || sortOption !== DEFAULT_SORT_OPTION);
  const recipeCountLabel =
    visibleRecipeCards.length === 1 ? '1 opskrift' : `${visibleRecipeCards.length} opskrifter`;

  const resetFilters = () => {
    setSearchTerm('');
    setSortOption(DEFAULT_SORT_OPTION);
  };

  const handleRecipeOpen = (recipeId) => {
    saveRecipeSearchState({
      searchTerm,
      sortOption,
      scrollRecipeId: recipeId
    });
  };

  return (
    <Container className="recipe-search-page editorial-page">
      <section className="recipe-search-intro recipe-search-hero editorial-hero">
        <p className="editorial-kicker">Opskriftsbibliotek</p>
        <h1 className="editorial-title">Opskrifter</h1>
        <p className="editorial-lead">Find retter og sorter dem efter det, der betyder mest for dig denne uge.</p>
        <div className="recipe-search-highlights" aria-label="Opskrifternes fokus">
          <strong>Bibliotekets fokus</strong>
          <span>Ugens tilbud</span>
          <span>Få ingredienser</span>
          <span>Fleksible retter</span>
        </div>
      </section>

      <section className="recipe-filters-card">
        <Row className="recipe-filters-row">
          <Col xs={12} md={5}>
            <Form.Group className="recipe-filter-group">
              <Form.Label>Søg efter ret</Form.Label>
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Fx pasta med tomat"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="recipe-filter-group">
              <Form.Label className="recipe-sort-label">
                <span>Sorter opskrifter efter</span>
                <ChevronDown size={14} aria-hidden="true" />
              </Form.Label>
              <div className="recipe-sort-select-wrap">
                <Form.Select
                  value={sortOption}
                  onChange={(event) => setSortOption(event.target.value)}
                  aria-label="Sorter opskrifter efter"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <ChevronDown size={18} aria-hidden="true" className="recipe-sort-select-icon" />
              </div>
            </Form.Group>
          </Col>
          <Col xs={12} md={3} className="recipe-filters-actions">
            <div className="recipe-filter-result-badge">
              <span className="recipe-filter-result-label">Viser nu</span>
              <p className="recipe-filter-result-count">
                {loading ? 'Finder opskrifter...' : recipeCountLabel}
              </p>
            </div>
            {hasActiveFilters && (
              <Button
                type="button"
                variant="outline-secondary"
                className="recipe-filter-reset"
                onClick={resetFilters}
              >
                Nulstil filtre
              </Button>
            )}
          </Col>
        </Row>
      </section>

      {loading && (
        <section className="recipe-state-card is-loading" aria-live="polite">
          <div className="recipe-state-icon" aria-hidden="true">...</div>
          <div>
            <h2>Henter opskrifter</h2>
            <p>Vi finder retterne og kobler dem med ugens tilbud.</p>
          </div>
        </section>
      )}
      {error && (
        <section className="recipe-state-card is-error" role="status">
          <div className="recipe-state-icon" aria-hidden="true">!</div>
          <div className="recipe-state-copy">
            <h2>Opskrifterne er ikke tilgængelige</h2>
            <p>{error} Prøv igen om lidt, eller gå tilbage til forsiden og planlæg ud fra butikkerne.</p>
            <div className="recipe-state-actions">
              <Button type="button" variant="outline-secondary" onClick={() => window.location.reload()}>
                Prøv igen
              </Button>
              <Link to="/" className="recipe-state-link">Til forsiden</Link>
            </div>
          </div>
        </section>
      )}

      {!loading && !error && (
        <Row className="recipe-card-grid">
          {visibleRecipeCards.map((card) => {
            const {
              recipe,
              recipeId,
              priceComparison,
              weeklySavingsLabel
            } = card;
            const imageCandidates = getRecipeImageCandidates(recipe);
            const primaryImage = imageCandidates[0] || PLACEHOLDER_IMAGE;
            const offerSavingsText = buildOfferSavingsText(weeklySavingsLabel);
            const showPantryComparison =
              priceComparison.hasPantrySavings || priceComparison.hasCompletePantryCoverage;
            return (
              <Col xs={12} md={6} lg={4} key={recipeId} className="recipe-card-col">
                <FavoriteButton
                  recipeId={recipeId}
                  title={recipe.name}
                  image={primaryImage}
                  className="recipe-card-favorite-button"
                  showLabel={false}
                />
                <Link
                  to={`/recipes/${recipeId}`}
                  className="recipe-preview-link"
                  onClick={() => handleRecipeOpen(recipeId)}
                >
                  <article className="recipe-preview-card" data-recipe-id={recipeId}>
                    <div className="recipe-preview-media">
                      <img
                        src={primaryImage}
                        alt={recipe.name || recipeId}
                        loading="lazy"
                        className="recipe-preview-image"
                        data-image-index="0"
                        data-image-candidates={JSON.stringify(imageCandidates)}
                        style={{ objectPosition: getRecipeImagePosition(recipeId) }}
                        onError={(event) => {
                          const imageElement = event.currentTarget;
                          const candidates = JSON.parse(imageElement.dataset.imageCandidates || '[]');
                          const currentIndex = Number(imageElement.dataset.imageIndex || '0');
                          const nextIndex = currentIndex + 1;

                          if (nextIndex < candidates.length) {
                            imageElement.dataset.imageIndex = `${nextIndex}`;
                            imageElement.src = candidates[nextIndex];
                            return;
                          }

                          imageElement.onerror = null;
                          imageElement.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                    </div>
                    <div className="recipe-preview-body">
                      <h3 className="recipe-preview-title">{recipe.name}</h3>
                      <div className="recipe-preview-pricing">
                        <div className="recipe-preview-price-compare">
                          <div
                            className={`recipe-preview-price-group${showPantryComparison ? ' recipe-preview-price-group-separated' : ''}`}
                          >
                            <div className={`recipe-preview-deal-pair${offerSavingsText ? '' : ' is-price-only'}`}>
                              <span className="recipe-preview-deal-price">
                                <small>Køb ind for</small>
                                <strong>{priceComparison.fullWeeklyLabel}</strong>
                              </span>
                              {offerSavingsText && (
                                <span className="recipe-preview-deal-savings">{offerSavingsText}</span>
                              )}
                            </div>
                          </div>
                          {showPantryComparison && (
                            <div
                              className={`recipe-preview-price-row recipe-preview-pantry-price${priceComparison.hasCompletePantryCoverage ? ' is-complete' : ''}`}
                            >
                              {priceComparison.hasCompletePantryCoverage ? (
                                <>
                                  <CheckCircleFill aria-hidden="true" />
                                  <strong>Alt er i dit spisekammer</strong>
                                </>
                              ) : (
                                <>
                                  <span>Med dit spisekammer</span>
                                  <strong>{priceComparison.pantryWeeklyLabel}</strong>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="recipe-preview-meta-row">
                        <p className="recipe-preview-time">
                          <Clock size={14} />
                          <span>{formatTimeRange(recipe.tid_min, recipe.tid_max) || 'Tid ukendt'}</span>
                        </p>
                        <p className="recipe-preview-people">
                          <People size={14} />
                          <span>
                            {normalize(recipe.portions)
                              ? `${normalize(recipe.portions)} pers.`
                              : 'Valgfri portion'}
                          </span>
                        </p>
                      </div>
                      <div className="recipe-preview-open-wrap">
                        <span className="recipe-preview-open">Åbn opskrift</span>
                      </div>
                    </div>
                  </article>
                </Link>
              </Col>
            );
          })}

          {visibleRecipeCards.length === 0 && (
            <Col xs={12}>
              <div className="recipe-empty-state">
                <h3>Ingen opskrifter matcher filtrene</h3>
                <p>Prøv at fjerne et filter eller søg på en anden ret.</p>
              </div>
            </Col>
          )}
        </Row>
      )}
    </Container>
  );
}

export default RecipeSearch;
